// The words a project's contract type uses (Marius round 4, OB-F8, X-63). The
// contract profile (contract-profile.ts) sets the rules per contract type; this
// sets the wording, so a grant does not read like a fee-based service contract:
// no "key experts", "Annex III" or "fee-based" first. One place, used by the
// onboarding sections and the New ToR form.

/** The contract types a project or a draft can have (a draft may not know it yet). */
export type WordingKind = "grant" | "service_fee_based" | "service_global_price" | "other" | null | undefined;

export const TOR_METHODS = ["fee_based", "lump_sum", "global_price"] as const;
export type TorMethod = (typeof TOR_METHODS)[number];

export type ContractWording = {
  /** The people the contract names: "Key experts" (service contract), "Key staff" (grant). */
  keyPeople: string;
  /** The hint under them. */
  keyPeopleHint: string;
  /** The budgeted people without names. */
  pools: string;
  poolsHint: string;
  poolsEmpty: string;
  /** Where the named team comes from, for the empty table. */
  teamSource: string;
  /** Where the budget comes from, for the empty budget. */
  budgetSource: string;
  /** A budget line of the kind "fees". */
  feesLabel: string;
  /** The ToR payment methods to offer, in order, with their words. */
  torMethods: { value: TorMethod; title: string; description: string }[];
};

const SERVICE_METHODS: Record<TorMethod, { title: string; description: string }> = {
  fee_based: { title: "Fee-based", description: "Expert days at the contract fee rates" },
  lump_sum: { title: "Lump sum", description: "A fixed amount" },
  global_price: { title: "Global price", description: "One price for the whole output" },
};

const methods = (order: TorMethod[], words: Record<TorMethod, { title: string; description: string }> = SERVICE_METHODS) => order.map((value) => ({ value, ...words[value] }));

const SERVICE: ContractWording = {
  keyPeople: "Key experts",
  keyPeopleHint: "Days and rates come from the budget lines.",
  pools: "Non-key expert pools",
  poolsHint: "Days and a rate per category. Names come later, with each ToR.",
  poolsEmpty: "The budget has no non-key expert pools.",
  teamSource: "the offer's team (Annex III)",
  budgetSource: "the budget breakdown (Annex V)",
  feesLabel: "Fees",
  torMethods: methods(["fee_based", "lump_sum", "global_price"]),
};

const GRANT: ContractWording = {
  keyPeople: "Key staff",
  keyPeopleHint: "The people the grant names. Days and rates come from the budget lines.",
  pools: "Other staff and experts",
  poolsHint: "Budgeted days per category, without names yet.",
  poolsEmpty: "The budget has no staff categories without names.",
  teamSource: "the team named in the grant application",
  budgetSource: "the grant's budget annex",
  feesLabel: "Staff costs",
  // A grant pays a fixed amount or real costs; days at a daily rate come last, in a grant's words.
  torMethods: methods(["lump_sum", "global_price", "fee_based"], {
    ...SERVICE_METHODS,
    fee_based: { title: "Days at a daily rate", description: "Days at the daily rates in the grant budget" },
    global_price: { title: "Fixed price", description: "One price for the whole output" },
  }),
};

export function contractWording(kind: WordingKind): ContractWording {
  if (kind === "grant") return GRANT;
  if (kind === "service_global_price") return { ...SERVICE, torMethods: methods(["global_price", "lump_sum", "fee_based"]) };
  return SERVICE;
}
